import type { EvalVideo, Event, GoldEventClip } from "@sitescene/types";

import {
	type InfiniteData,
	useInfiniteQuery,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";

import { apiClient, type ApiResponse } from "@/api/client";
import { ENDPOINTS } from "@/api/endpoints";
import { queryKeys } from "@/api/query-keys";
import { useOrgSelection } from "@/context/use-org-selection";

/** Clips to copy, and the gold standard fields to start each copy with. */
export interface AddToGoldInput {
	clips: { durationSeconds?: number; mediaKey: string }[];
	description?: string;
	severity?: number;
}

/** The Events page's filters, as the API takes them. */
export interface EventFilters {
	endTimestamp?: string;
	severityMin?: string;
	startTimestamp?: string;
	status?: string;
	title?: string;
}

const PAGE_SIZE = 50;

const useOrgCode = () => useOrgSelection().selected?.orgCode ?? null;

/** Events of the selected organization, newest first, paged by cursor. */
export const useEventSearch = (filters: EventFilters) => {
	const orgCode = useOrgCode();
	const activeFilters = Object.fromEntries(
		Object.entries(filters).filter(([, value]) => Boolean(value)),
	) as Record<string, string>;

	return useInfiniteQuery<
		ApiResponse<Event[]>,
		Error,
		InfiniteData<ApiResponse<Event[]>>,
		readonly unknown[],
		string | undefined
	>({
		enabled: orgCode !== null,
		getNextPageParam: (lastPage) =>
			lastPage.hasMore ? lastPage.endCursor : undefined,
		initialPageParam: undefined,
		queryFn: ({ pageParam }) =>
			apiClient.get<Event[]>(ENDPOINTS.shadow.events.search, {
				params: { ...activeFilters, after: pageParam, limit: PAGE_SIZE },
			}),
		queryKey: queryKeys.events.list(orgCode ?? "", activeFilters),
	});
};

/**
 * The event's clips with signed playback URLs, held for most of their hour
 * like the other clip URLs.
 */
export const useEventClips = (eventId: string | undefined) => {
	const orgCode = useOrgCode();

	return useQuery({
		enabled: orgCode !== null && Boolean(eventId),
		gcTime: 0,
		queryFn: async () => {
			const response = await apiClient.get<GoldEventClip[]>(
				ENDPOINTS.shadow.events.clips(eventId ?? ""),
			);
			return response.data;
		},
		queryKey: queryKeys.events.clips(orgCode ?? "", eventId ?? ""),
		retry: false,
		staleTime: 50 * 60 * 1000,
	});
};

/** Copy picked clips into the gold standard set. Resolves with the new videos. */
export const useAddClipsToGold = (eventId: string) => {
	const queryClient = useQueryClient();
	const orgCode = useOrgCode() ?? "";

	return useMutation({
		mutationFn: async (input: AddToGoldInput) => {
			const response = await apiClient.post<EvalVideo[]>(
				ENDPOINTS.shadow.events.goldVideos(eventId),
				input,
			);
			return response.data;
		},
		onSuccess: () =>
			Promise.all([
				queryClient.invalidateQueries({
					queryKey: queryKeys.events.clips(orgCode, eventId),
				}),
				queryClient.invalidateQueries({ queryKey: queryKeys.goldVideos.all }),
			]),
	});
};
